// Carpet supplier B2B portals (26 Oct 2026, docs/SUPPLIER-HUB.md §6). Jacky's design pick: supplier stock on the supplier
// page, on the Job/PO carpet line and on the staff Proposal colour pick. It is the SUPPLIER's shelf as EmmA read it from the
// portal this morning — never ours (that is /inventory) — and nothing here is customer-facing.
import {Fragment, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {DataTable, TableSkeleton, Tag, Td} from './aligned-table';
import {useT} from '../lib/i18n';
import {dayText, getData, metres, sydneyStamp, useSupplierStockFor, type PortalRead, type StockColour, type StockRoll} from '../lib/supplier-stock';
import {SupplierStockLine} from './supplier-stock-line';
export {SupplierStockLine};


/** Under a PO line or a Proposal colour: what the supplier holds of exactly this colour. Silent when its portal is not read. */
export function SupplierStockNote({variantId, className}: {variantId: string | null | undefined; className?: string}) {
  const {t} = useT();
  const q = useSupplierStockFor(variantId);
  const c = q.data?.colours[0];
  if (!c) return null;
  return <span className={className ?? 'block text-micro'}><SupplierStockLine colour={c} t={t}/></span>;
}

const CHANGE_TONE: Record<string, 'warn' | 'ok' | 'info' | undefined> = {up: 'warn', down: 'ok', mixed: 'warn', agreed: 'info'};

/** The supplier page's "Supplier stock" tab: the morning read, its cost report, and every colour (rolls on click). */
export function SupplierPortalStock({supplierId, read}: {supplierId: string; read: PortalRead}) {
  const {t} = useT();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const colours = useQuery({queryKey: ['supplier-stock', supplierId], queryFn: () => getData<{colours: StockColour[]}>(`/api/supplier-stock?supplier_id=${encodeURIComponent(supplierId)}&limit=2000`)});
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const rows = (colours.data?.colours ?? []).filter((c) => words.every((w) => `${c.collection} ${c.colour} ${c.code ?? ''}`.toLowerCase().includes(w)));
  return <div className="space-y-4">
    <p className="text-sm text-muted-foreground">
      {t('sup.stock.readAt', {portal: read.portal, at: sydneyStamp(read.read_at)})} · {t(read.prices_changed ? 'sup.stock.pricesChanged' : 'sup.stock.pricesSame')} ·{' '}
      {read.costs_applied_at ? t('sup.stock.costsApplied', {at: sydneyStamp(read.costs_applied_at)}) : t('sup.stock.costsNotApplied')}
    </p>
    {read.report.length > 0 && <>
      <h3 className="text-sm font-semibold">{t('sup.stock.report')}</h3>
      <DataTable density="compact" cols={[{label: t('sup.stock.col.product')}, {label: t('sup.stock.col.portalLm'), width: 110, num: true, hint: t('sup.exGstHint')},
        {label: t('sup.stock.col.portalM2'), width: 110, num: true}, {label: t('sup.stock.col.catalogueM2'), width: 130, num: true}, {label: t('sup.stock.col.change'), width: 150}]}>
        {read.report.map((r) => <tr key={r.product_id}>
          <Td>{r.product}</Td>
          <Td num>{r.portal_price_per_lm === null ? '—' : `$${r.portal_price_per_lm.toFixed(2)}`}</Td>
          <Td num>{r.portal_cost_per_m2 === null ? '—' : `$${r.portal_cost_per_m2.toFixed(2)}`}</Td>
          <Td num dim>{r.catalogue_cost_per_m2.length ? r.catalogue_cost_per_m2.map((n) => `$${n.toFixed(2)}`).join(' / ') : '—'}</Td>
          {/* The report is what the read found BEFORE the apply; once applied, a moved product says so (live 26 Oct: Pizazz read "Portal lower" after it was updated). */}
          <Td>{read.costs_applied_at && r.colours_to_move > 0 ? <Tag tone="ok">{t('sup.stock.change.applied')}</Tag> : <Tag tone={CHANGE_TONE[r.change]}>{t(`sup.stock.change.${r.change}`)}</Tag>}</Td>
        </tr>)}
      </DataTable>
    </>}
    <div className="flex flex-wrap items-center gap-2">
      <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('sup.stock.search')} aria-label={t('sup.stock.search')}
        className="w-72 rounded-md border bg-background px-2 py-1.5 text-sm"/>
      <span className="text-micro text-muted-foreground">{t('sup.stock.shown', {count: rows.length, colours: read.colours, rolls: read.rolls})}</span>
    </div>
    <DataTable density="compact" cols={[{label: t('sup.stock.col.range')}, {label: t('sup.stock.col.colour')}, {label: t('sup.stock.col.code'), width: 90, mono: true},
      {label: t('sup.stock.col.metres'), width: 90, num: true}, {label: t('sup.stock.col.rolls'), width: 70, num: true}, {label: t('sup.stock.col.largest'), width: 100, num: true},
      {label: t('sup.stock.col.dyeLots'), width: 80, num: true}, {label: t('sup.stock.col.next'), width: 110}]}>
      {colours.isLoading && <TableSkeleton cols={8}/>}
      {rows.map((c) => {
        const key = `${c.collection}|${c.colour}`;
        return <Fragment key={key}>
          <tr className="cursor-pointer hover:bg-muted/40" onClick={() => setOpen(open === key ? null : key)} aria-expanded={open === key}>
            <Td>{c.collection}</Td>
            <Td>{c.colour}{!c.variant_id && <span className="ml-1.5"><Tag>{t('sup.stock.notInCatalogue')}</Tag></span>}</Td>
            <Td mono dim>{c.code ?? '—'}</Td>
            <Td num className={c.available_lm > 0 ? undefined : 'text-warning'}>{metres(c.available_lm)}</Td>
            <Td num>{c.rolls}</Td>
            <Td num dim>{metres(c.largest_roll_lm)}</Td>
            <Td num dim>{c.dye_lots}</Td>
            <Td dim>{c.next_production ? dayText(c.next_production) : '—'}</Td>
          </tr>
          {open === key && <tr><td colSpan={8} className="bg-muted/20 px-3 py-2"><Rolls supplierId={supplierId} colour={c}/></td></tr>}
        </Fragment>;
      })}
      {!colours.isLoading && rows.length === 0 && <tr><Td colSpan={8} className="py-6 text-center" dim>{t('sup.stock.empty')}</Td></tr>}
    </DataTable>
    <p className="text-xs text-muted-foreground">{t('sup.stock.footnote')}</p>
  </div>;
}

function Rolls({supplierId, colour}: {supplierId: string; colour: StockColour}) {
  const {t} = useT();
  const q = useQuery({queryKey: ['supplier-stock-rolls', supplierId, colour.collection, colour.colour], queryFn: () =>
    getData<{rolls: StockRoll[]}>(`/api/supplier-stock?supplier_id=${encodeURIComponent(supplierId)}&rolls=1&limit=500&q=${encodeURIComponent(`${colour.collection} ${colour.colour}`)}`)});
  const rolls = (q.data?.rolls ?? []).filter((r) => r.collection === colour.collection && r.colour === colour.colour && r.roll_no);
  if (q.isLoading) return <p role="status" className="text-micro text-muted-foreground">{t('sup.loading')}</p>;
  if (!rolls.length) return <p className="text-micro text-muted-foreground"><SupplierStockLine colour={colour} t={t}/></p>;
  return <table className="w-full text-xs">
    <thead><tr className="text-left text-muted-foreground"><th className="py-1 font-normal">{t('sup.stock.col.roll')}</th><th className="font-normal">{t('sup.stock.col.dyeLot')}</th>
      <th className="font-normal">{t('sup.stock.col.location')}</th><th className="text-right font-normal">{t('sup.stock.col.metres')}</th><th className="text-right font-normal">{t('sup.stock.col.price')}</th></tr></thead>
    <tbody>{rolls.map((r) => <tr key={r.roll_no!} className="border-t border-border/50">
      <td className="py-1 font-mono">{r.roll_no}</td><td className="font-mono">{r.dye_lot ?? '—'}</td><td>{r.location ?? '—'}</td>
      <td className="text-right tabular-nums">{metres(r.available_lm)}</td>
      <td className="text-right tabular-nums">{r.price_per_lm === null ? '—' : `$${r.price_per_lm.toFixed(2)}`}{r.price_note && <span className="ml-1 text-muted-foreground">({r.price_note})</span>}</td>
    </tr>)}</tbody>
  </table>;
}
